/**
 * Telemetry Math Utilities
 */

/**
 * Linearly interpolate a value between two points.
 */
export function lerp(x: number, x0: number, x1: number, y0: number, y1: number): number {
  if (x1 === x0) return y0;
  return y0 + (x - x0) * (y1 - y0) / (x1 - x0);
}

/**
 * Interpolate an array of values to a new set of x-coordinates.
 * OT-134: Handles non-monotonic x input (e.g. LapDistPct resetting at finish line)
 * by sorting x/y pairs first. Assumes newX is sorted.
 */
export function interpolateArray(x: number[], y: number[], newX: number[]): number[] {
  if (x.length === 0 || y.length === 0) return new Array(newX.length).fill(0);

  // OT-134: Sort by x to handle finish-line discontinuities in LapDistPct
  // Detect if sorting is needed (check for any decrease)
  let needsSort = false;
  for (let i = 1; i < x.length; i++) {
    if (x[i] < x[i - 1]) { needsSort = true; break; }
  }

  let sx = x, sy = y;
  if (needsSort) {
    const indices = Array.from({ length: x.length }, (_, i) => i);
    indices.sort((a, b) => x[a] - x[b]);
    sx = indices.map(i => x[i]);
    sy = indices.map(i => y[i]);
  }

  const result = new Array(newX.length);
  let j = 0;

  for (let i = 0; i < newX.length; i++) {
    const target = newX[i];

    // Find the interval in sx that contains target
    while (j < sx.length - 2 && sx[j + 1] < target) {
      j++;
    }

    result[i] = lerp(target, sx[j], sx[j + 1], sy[j], sy[j + 1]);
  }

  return result;
}

/**
 * Generate a fixed distance axis from 0 to 1 with N points.
 */
export function generateDistanceAxis(points: number = 1000): number[] {
  const axis = new Array(points);
  for (let i = 0; i < points; i++) {
    axis[i] = i / (points - 1);
  }
  return axis;
}
